/**
 * Sentry 초기화 유틸리티
 * - DSN 미설정 시 no-op (로컬 개발 환경에서 Sentry 전송 방지)
 * - tracesSampleRate: 10% — 번들 크기·쿼터 절약
 * - replaysSessionSampleRate: 5% / replaysOnErrorSampleRate: 100%
 * - beforeSend: 민감 필드명 scrub + URL 쿼리 파라미터 마스킹
 * - replayIntegration: analytics 쿠키 동의 시에만 활성화 (GDPR)
 *
 * ─── PII 마스킹 정책 ───────────────────────────────────────────────
 * Session Replay는 기본적으로 모든 텍스트·입력값·미디어를 차단한다.
 *   maskAllText:   true  — 모든 텍스트 노드 마스킹 (이메일·앨범 제목·댓글 등)
 *   maskAllInputs: true  — 모든 <input>·<textarea> 마스킹
 *   blockAllMedia: true  — 이미지·video·canvas 등 미디어 차단
 *
 * 특정 요소를 화이트리스트로 마스킹 해제하려면:
 *   1. HTML 요소에 class="sentry-unmask" 또는 data-sentry-unmask 속성 추가
 *   2. 앨범 제목·이메일·댓글·개인정보가 포함된 요소는 절대 언마스킹 금지
 *   3. 언마스킹 가능 예시: UI 버튼 라벨, 페이지 제목, 공개 에러 메시지
 * ────────────────────────────────────────────────────────────────────
 */
import * as Sentry from '@sentry/react';
import type { Consent } from '../hooks/useCookieConsent';

/** 초기화 완료 여부 (중복 호출 방지) */
let initialized = false;

/** Replay가 이미 활성화됐는지 여부 */
let replayActivated = false;

/**
 * Sentry 초기화. main.tsx 최상단에서 한 번만 호출한다.
 * analytics 쿠키 동의가 있을 때만 replayIntegration을 포함한다.
 *
 * @param dsn                - VITE_SENTRY_DSN 환경변수 값. 빈 문자열이면 no-op.
 * @param analyticsConsented - 쿠키 동의 여부. true 일 때만 Replay 활성화.
 */
export function initSentry(dsn: string | undefined, analyticsConsented = false): void {
  if (!dsn) {
    // DSN 미설정 — 로컬 개발 또는 스테이징에서 의도적으로 비활성화
    console.debug('[Sentry] VITE_SENTRY_DSN 미설정 — Sentry 비활성화');
    return;
  }

  if (initialized) return;
  initialized = true;

  const environment = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE ?? 'development';

  // analytics 동의 시에만 Replay 통합 포함
  const integrations: Sentry.Integration[] = [
    // 페이지 로드·탐색·XHR·fetch 자동 추적
    Sentry.browserTracingIntegration(),
  ];

  if (analyticsConsented) {
    // Session Replay (마우스·클릭·스크롤 기록) — 동의한 경우에만 추가
    // GDPR 최소 수집 원칙: 모든 텍스트·입력·미디어 기본 차단
    integrations.push(
      Sentry.replayIntegration({
        // 모든 텍스트 노드 마스킹 — 이메일·앨범 제목·댓글 등 PII 보호
        maskAllText: true,
        // 모든 <input>·<textarea> 마스킹 — 사용자 입력값 PII 보호
        maskAllInputs: true,
        // 이미지·video·canvas 등 미디어 차단 — 앨범 커버 등 민감 콘텐츠 보호
        blockAllMedia: true,
        // 마스킹 예외 selector (화이트리스트):
        //   안전한 요소(버튼 라벨, 페이지 제목 등)에만 적용할 것.
        //   앨범 제목·이메일·댓글 포함 요소는 절대 추가 금지.
        unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
      }),
    );
    replayActivated = true;
  }

  Sentry.init({
    dsn,
    environment,

    // 환경별 트랜잭션 샘플링: prod 10%(쿼터 절약), staging/dev 50%(추적 증강)
    tracesSampleRate: environment === 'production' ? 0.1 : 0.5,

    // Session Replay: 세션 5%, 에러 발생 시 100%
    // analytics 미동의 시 0으로 설정하여 수집 방지
    replaysSessionSampleRate: analyticsConsented ? 0.05 : 0,
    replaysOnErrorSampleRate: analyticsConsented ? 1.0 : 0,

    integrations,

    /**
     * beforeSend: 이벤트 전송 전 민감 정보 scrub
     * - 민감 필드명 재귀 제거 (password, token, secret 등)
     * - URL 쿼리 파라미터에서 token=/refreshToken= 마스킹
     * - Authorization 헤더 마스킹
     * - breadcrumbs URL 쿼리 파라미터 마스킹
     */
    beforeSend(event) {
      return scrubEvent(event);
    },
  });

  // 초기화 후 cookieConsentChange 이벤트 수신 — 동적 동의 변경 감지
  window.addEventListener('cookieConsentChange', (e: Event) => {
    const consent = (e as CustomEvent<Consent>).detail;
    if (consent.analytics && !replayActivated) {
      // 동의 후 Replay 활성화 — Sentry는 런타임 integration 추가를 지원하지 않으므로
      // 페이지 새로고침을 권장한다. 다음 세션부터 Replay가 활성화된다.
      replayActivated = true;
      console.info(
        '[Sentry] analytics 동의 감지 — 다음 페이지 로드부터 Session Replay가 활성화됩니다.',
      );
    }
  });
}

// ─── scrub 로직 ──────────────────────────────────────────────────────────────

/**
 * 민감 필드명 패턴.
 * 매칭 시 해당 필드 값을 '[Filtered]'로 교체한다.
 */
export const SENSITIVE_KEYS =
  /password|token|refreshToken|accessToken|secret|apiKey|api.?key|creditCard|credit.?card|ssn|authorization/i;

/**
 * URL 쿼리 스트링에서 민감 파라미터 값을 마스킹하는 정규식.
 * 예) ?token=abc123 → ?token=[Filtered]
 */
const SENSITIVE_QUERY_PARAMS = /((?:^|[&?])(?:token|refreshToken|accessToken)=)[^&]*/gi;

/**
 * URL 문자열에서 민감 쿼리 파라미터를 마스킹한다.
 */
export function maskSensitiveUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  return url.replace(SENSITIVE_QUERY_PARAMS, '$1[Filtered]');
}

/**
 * 이벤트 객체에서 민감한 필드명을 재귀적으로 제거한다.
 * 필드명 매칭은 대소문자 무시.
 */
export function scrubSensitiveData(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(scrubSensitiveData);
  }

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (SENSITIVE_KEYS.test(key)) {
      result[key] = '[Filtered]';
    } else {
      result[key] = scrubSensitiveData(value);
    }
  }
  return result;
}

/**
 * Sentry 이벤트 전체 scrub 처리:
 * 1. 필드명 기반 재귀 scrub
 * 2. request.url 쿼리 파라미터 마스킹
 * 3. request.headers.Authorization 마스킹
 * 4. breadcrumbs data.url 쿼리 파라미터 마스킹
 */
function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // 1. 전체 이벤트 재귀 scrub (민감 필드명 제거)
  const scrubbed = scrubSensitiveData(event) as Sentry.ErrorEvent;

  // 2. request.url 쿼리 파라미터 마스킹
  if (scrubbed.request?.url) {
    scrubbed.request.url = maskSensitiveUrl(scrubbed.request.url) ?? scrubbed.request.url;
  }

  // 3. request.headers Authorization 마스킹
  if (scrubbed.request?.headers) {
    const headers = scrubbed.request.headers as Record<string, string>;
    if (headers['Authorization']) {
      headers['Authorization'] = '[Filtered]';
    }
    if (headers['authorization']) {
      headers['authorization'] = '[Filtered]';
    }
  }

  // 4. breadcrumbs data.url 쿼리 파라미터 마스킹
  if (scrubbed.breadcrumbs?.values) {
    scrubbed.breadcrumbs.values = scrubbed.breadcrumbs.values.map((crumb) => {
      if (crumb.data?.url && typeof crumb.data.url === 'string') {
        return {
          ...crumb,
          data: {
            ...crumb.data,
            url: maskSensitiveUrl(crumb.data.url) ?? crumb.data.url,
          },
        };
      }
      return crumb;
    });
  }

  return scrubbed;
}

// ─── 외부 헬퍼 ───────────────────────────────────────────────────────────────

/**
 * 에러를 Sentry에 캡처하는 헬퍼.
 * telemetry.ts의 reportError 등에서 병행 호출한다.
 * Sentry 미초기화 상태에서는 no-op.
 *
 * @param error   - 캡처할 에러 객체
 * @param context - 추가 컨텍스트 (pageName, userId 등)
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;

  Sentry.captureException(error, {
    extra: context,
  });
}
